import {MessageDescriptor, defineMessages} from 'react-intl'

/**
 * Mensajes compartidos por las pantallas de telefonía de la Fase 1.10
 * (`numbers`, `twilioBilling`, `assignmentGroups`, `channelConfig`,
 * `telephonySupervisor`).
 *
 * Viven aquí —y no en el catálogo de una carpeta— porque son el mismo texto en
 * varias pantallas: si cada slice declarara el suyo, `dups telephony` los
 * reportaría como valores repetidos dentro del namespace. Lo genérico de toda
 * la app (Cancelar, Buscar, Anterior/Siguiente…) sigue en `common.*`; aquí solo
 * va lo que es específico de telefonía.
 */
export const TELEPHONY_MESSAGES = defineMessages({
  assign: {id: 'telephony.action.assign', defaultMessage: 'Assign'},
  release: {id: 'telephony.action.release', defaultMessage: 'Release'},
  buy: {id: 'telephony.action.buy', defaultMessage: 'Buy'},
  selectState: {id: 'telephony.placeholder.selectState', defaultMessage: 'Select a state…'},
  campaign: {id: 'telephony.field.campaign', defaultMessage: 'Campaign'},
  direction: {id: 'telephony.field.direction', defaultMessage: 'Direction'},
  list: {id: 'telephony.field.list', defaultMessage: 'List'},
  test: {id: 'telephony.action.test', defaultMessage: 'Test'},
  window: {id: 'telephony.field.window', defaultMessage: 'Window'},
  labelCount: {id: 'telephony.labelCount', defaultMessage: '{label}: {count}'},
  errorSavingChannel: {
    id: 'telephony.channels.errorSaving',
    defaultMessage: 'Error saving channel',
  },

  // ── PhoneProviderActions ──
  // El selector de llamada/SMS que se pinta junto a cada teléfono. Lo renderizan
  // 30 archivos de casi todas las carpetas ya cerradas, asi que su texto salia
  // en ingles en media aplicacion (slice 1.11.d).
  noPhoneNumber: {
    id: 'telephony.phone.noNumber',
    defaultMessage: 'This record has no phone number.',
  },
  rcNotConnected: {
    id: 'telephony.phone.rcNotConnected',
    defaultMessage: 'RingCentral is not connected in this browser.',
  },
  rcStillLoading: {
    id: 'telephony.phone.rcStillLoading',
    defaultMessage: 'RingCentral is still loading. Try again in a moment.',
  },
  rcSmsOpened: {
    id: 'telephony.phone.rcSmsOpened',
    defaultMessage: 'SMS composer opened in RingCentral.',
  },
  dialNotConnected: {
    id: 'telephony.phone.dialNotConnected',
    defaultMessage: 'Connect PolicyDial from Integrations before starting a call.',
  },
  /** `{provider}` es una marca: no se traduce, solo el envoltorio. */
  providerOptions: {
    id: 'telephony.phone.providerOptions',
    defaultMessage: '{provider} options',
  },
  callWithProvider: {
    id: 'telephony.phone.callWith',
    defaultMessage: 'Call with {provider}',
  },
  textWithProvider: {
    id: 'telephony.phone.textWith',
    defaultMessage: 'Text with {provider}',
  },

  // ── Reproductor de grabaciones (`VicidialCallRecordingPlayer`) ──
  /**
   * Los tres primeros se lanzan con `new Error` y **sí** llegan al usuario:
   * el `catch` guarda su `.message` en `error`, que se pinta bajo el botón.
   */
  errRecordingNotFound: {
    id: 'telephony.recording.error.notFound',
    defaultMessage: 'Recording not found',
  },
  errRecordingUrlMissing: {
    id: 'telephony.recording.error.urlMissing',
    defaultMessage: 'Recording URL missing',
  },
  errRecordingLoad: {
    id: 'telephony.recording.error.load',
    defaultMessage: 'Unable to load recording',
  },
  downloadRecording: {
    id: 'telephony.recording.download',
    defaultMessage: 'Download recording',
  },
  playRecording: {id: 'telephony.recording.play', defaultMessage: 'Play recording'},
  download: {id: 'leads.kanban.downloadTooltip', defaultMessage: 'Download'},
  loading: {id: 'common.loading', defaultMessage: 'Loading...'},

  // ── Botón flotante ──
  notificationIncoming: {
    id: 'telephony.floating.notificationIncoming',
    defaultMessage: 'Incoming PolicyDial call',
  },
  incomingClickToAnswer: {
    id: 'telephony.floating.incomingClickToAnswer',
    defaultMessage: 'Incoming call — click to answer',
  },
  notificationBody: {
    id: 'telephony.floating.notificationBody',
    defaultMessage: 'Click to open PolicyDial and answer.',
  },
  /** Título de la pestaña mientras entra una llamada. El emoji es el aviso. */
  tabIncoming: {
    id: 'telephony.floating.tabIncoming',
    defaultMessage: '📞 Incoming call — PolicyDial',
  },
  hideWebphone: {
    id: 'telephony.floating.hideWebphone',
    defaultMessage: 'Hide PolicyDial Webphone',
  },
  showWebphone: {
    id: 'telephony.floating.showWebphone',
    defaultMessage: 'Show PolicyDial Webphone',
  },
  openWebphone: {
    id: 'telephony.floating.openWebphone',
    defaultMessage: 'Open PolicyDial Webphone',
  },
  incomingCallFrom: {
    id: 'telephony.floating.incomingFrom',
    defaultMessage: 'Incoming PolicyDial call: {caller}',
  },
  callActive: {id: 'telephony.floating.callActive', defaultMessage: 'PolicyDial call active'},
  callActiveWith: {
    id: 'telephony.floating.callActiveWith',
    defaultMessage: 'PolicyDial call active: {caller}',
  },

  // ── Modal de disposición ──
  dispositionTitle: {
    id: 'telephony.disposition.title',
    defaultMessage: 'PolicyDial Disposition',
  },
  dispositionIntro: {
    id: 'telephony.disposition.intro',
    defaultMessage: 'The PolicyDial call ended. Save the disposition code before continuing.',
  },
  dispositionCode: {
    id: 'telephony.disposition.code',
    defaultMessage: 'Disposition code',
  },
  dispositionSelect: {
    id: 'telephony.disposition.select',
    defaultMessage: 'Select a disposition...',
  },
  dispositionNotes: {
    id: 'telephony.disposition.notes',
    defaultMessage: 'Optional notes for PolicyDial',
  },
  callbackDateTime: {
    id: 'telephony.disposition.callbackDateTime',
    defaultMessage: 'Callback date and time',
  },
  callbackRequired: {
    id: 'telephony.disposition.callbackRequired',
    defaultMessage: 'Required for {code} dispositions.',
  },
  submitDisposition: {
    id: 'telephony.disposition.submit',
    defaultMessage: 'Submit disposition',
  },
  badgePhone: {id: 'telephony.disposition.badge.phone', defaultMessage: 'Phone: {value}'},
  badgeEmail: {id: 'telephony.disposition.badge.email', defaultMessage: 'Email: {value}'},
  badgeVendorId: {
    id: 'telephony.disposition.badge.vendorId',
    defaultMessage: 'Vendor ID: {value}',
  },
  badgeLeadId: {id: 'telephony.disposition.badge.leadId', defaultMessage: 'Lead ID: {value}'},
  badgeCampaign: {
    id: 'telephony.disposition.badge.campaign',
    defaultMessage: 'Campaign: {value}',
  },
  badgeSession: {id: 'telephony.disposition.badge.session', defaultMessage: 'Session: {value}'},
  comments: {id: 'common.field.comments', defaultMessage: 'Comments'},
  saving: {id: 'common.state.saving', defaultMessage: 'Saving...'},
})

/**
 * Etiquetas de las disposiciones de PolicyDial. **El `code` es el que viaja a
 * la API** —es el código de Vicidial—; solo se traduce la etiqueta. La lista
 * original mezclaba inglés y español (`No Interesado`, `Buzón`, `Dead Air`):
 * el inglés queda como fuente y el español, coherente.
 */
export const DISPOSITION_LABELS = defineMessages({
  SALE: {id: 'telephony.disposition.opt.sale', defaultMessage: 'Sold'},
  DEC: {id: 'telephony.disposition.opt.dec', defaultMessage: 'Not Sold'},
  NINT: {id: 'telephony.disposition.opt.nint', defaultMessage: 'Not Interested'},
  NAPL: {id: 'telephony.disposition.opt.napl', defaultMessage: 'Not Applicable'},
  CALLBK: {id: 'telephony.disposition.opt.callbk', defaultMessage: 'Scheduled Callback'},
  NANS: {id: 'telephony.disposition.opt.nans', defaultMessage: 'No Answer'},
  N: {id: 'telephony.disposition.opt.n', defaultMessage: 'No Answer (auto)'},
  B: {id: 'telephony.disposition.opt.b', defaultMessage: 'Busy'},
  A: {id: 'telephony.disposition.opt.a', defaultMessage: 'Answering Machine'},
  VMAIL: {id: 'telephony.disposition.opt.vmail', defaultMessage: 'Voicemail'},
  HUPNR: {id: 'telephony.disposition.opt.hupnr', defaultMessage: 'Hung Up, No Response'},
  DAIR: {id: 'telephony.disposition.opt.dair', defaultMessage: 'Dead Air'},
  DC: {id: 'telephony.disposition.opt.dc', defaultMessage: 'Disconnected Number'},
  WRNUM: {id: 'telephony.disposition.opt.wrnum', defaultMessage: 'Wrong Number'},
  DNC: {id: 'telephony.disposition.opt.dnc', defaultMessage: 'DO NOT CALL'},
  XFER: {id: 'telephony.disposition.opt.xfer', defaultMessage: 'Call Transferred'},
  CHATR: {id: 'telephony.disposition.opt.chatr', defaultMessage: 'Chat - Resolved'},
  CHATNR: {id: 'telephony.disposition.opt.chatnr', defaultMessage: 'Chat - Not Resolved'},
  EMAIR: {id: 'telephony.disposition.opt.emair', defaultMessage: 'Email - Resolved'},
  EMAINR: {id: 'telephony.disposition.opt.emainr', defaultMessage: 'Email - Not Resolved'},
  EMAIF: {id: 'telephony.disposition.opt.emaif', defaultMessage: 'Email - For Follow Up'},
}) as Record<string, MessageDescriptor>

/** Separador entre el código y la etiqueta de la disposición. Símbolo. */
export const EM_DASH_SEP = ' — '

/** Prefijo del identificador de grabación. Símbolo, no texto. */
export const HASH = '#'

/** Marcas de los proveedores de telefonía. No se traducen. */
export const PROVIDER = {ringCentral: 'RingCentral', policyDial: 'PolicyDial'} as const
